import { yupResolver } from "@hookform/resolvers/yup";
import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ModalProps } from "..";
import { Button, Input, Modal } from "../../components";
import { LocalStorageItems } from "../../constants";
import useGetUser from "../../hooks/useGetUser";
import DefaultDetailsConfirmationModal from "../../pages/DonationPage/default-detail-model/DefaultDetailsConfirmationModel";
import { updateUserProfile } from "../../Services_V2/Auth.service";
import { getMemberByEmail_V2 } from "../../Services_V2/Member.service";
import { getLocalStorageItem } from "../../utils/localStorage";
import { IUserInputs, UserInputs, userInputSchema } from "./const";
import styles from "./details-modal.module.css";

interface IProps extends ModalProps {
  onSubmit: (userDetails: IUserInputs, totalAmount?: number) => void;
  isPanRequired: boolean;
  loading?: boolean;
  email?: string;
}

const DetailsModal: React.FC<IProps> = ({
  open,
  onClose,
  onSubmit,
  isPanRequired,
  loading,
  email,
}) => {
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [memberDetails, setMemberDetails] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getUser = useGetUser();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    clearErrors,
    getValues,
  } = useForm<IUserInputs>({
    resolver: yupResolver(userInputSchema),
    context: { isPanRequired: isPanRequired },
    defaultValues: {
      userType: "Individual",
      name: "",
      email: "",
      phone: "",
      pan_number: "",
      position: "",
    },
  });

  const fetchMemberDetails = useCallback(async () => {
    if (!open) return;
    try {
      let memberData: any = null;

      if (
        email &&
        typeof email === "string" &&
        email.trim() !== "" &&
        email.trim().toLowerCase() !== "null" &&
        email.trim().toLowerCase() !== "undefined"
      ) {
        const res = await getMemberByEmail_V2({ email });
        if (res?.data) {
          memberData = res.data;
        }
      } else {
        const storedData = getLocalStorageItem(
          LocalStorageItems.MEMBER_DETAILS,
        );
        if (storedData) {
          memberData = JSON.parse(storedData);
        }
      }

      if (memberData) {
        setMemberDetails(memberData);
      }
    } catch (error) {
      console.error("Failed to fetch member details:", error);
    }
  }, [email, open]);

  useEffect(() => {
    fetchMemberDetails();
  }, [fetchMemberDetails]);

  useEffect(() => {
    if (!memberDetails || !open) return;

    reset({
      userType:
        memberDetails.userType === "Corporate" ||
        memberDetails.userType === "Individual"
          ? memberDetails.userType
          : "Individual",
      name: memberDetails.name || "",
      email: memberDetails.email || "",
      phone: memberDetails.phone ? String(memberDetails.phone) : "",
      pan_number: memberDetails.pan_number || "",
      position: memberDetails.position || "",
    });
  }, [memberDetails, open, reset]);

  useEffect(() => {
    if (!open) {
      reset({
        userType: "Individual",
        name: "",
        email: "",
        phone: "",
        pan_number: "",
        position: "",
      });
      clearErrors();
    }
  }, [open, reset, clearErrors]);

  // const originalDefaults = useMemo(
  //   () => ({
  //     name: String(user?.name || ""),
  //     email: String(user?.email || ""),
  //     phone: String(user?.phone || ""),
  //     pan_number: String(user?.pan_number || ""),
  //     position: String(user?.position || ""),
  //     userType: String(user?.userType || "Individual"),
  //   }),
  //   [user]
  // );

  // const hasDetailsChanged = (values: IUserInputs, amount?: number) => {
  //   const isHighAmount = amount ? amount > 50000 : false;

  //   return (
  //     values?.name.trim() !== originalDefaults?.name.trim() ||
  //     values.email.trim() !== originalDefaults.email.trim() ||
  //     values.phone.trim() !== originalDefaults.phone.trim() ||
  //     values.userType !== originalDefaults.userType ||
  //     (values[UserInputs.POSITION] ?? "").trim() !==
  //       originalDefaults.position.trim() ||
  //     (isHighAmount &&
  //       (values.pan_number ?? "").trim() !== originalDefaults.pan_number.trim())
  //   );
  // };

  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const userType = watch(UserInputs.USER_TYPE);

  const onFormSubmit = async (values: IUserInputs) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmUpdate = async () => {
    const values = getValues();
    setUpdatingProfile(true);

    try {
      const payload: Record<string, any> = {};

      if (values.name?.trim()) {
        payload.name = values.name.trim();
      }

      if (values.phone?.toString().trim()) {
        payload.phone = values.phone.toString().trim();
      }

      if (values.userType) {
        payload.userType = values.userType;
      }

      if (values.pan_number?.trim()) {
        payload.pan_number = values.pan_number.trim();
      }

      if (values.userType === "Corporate" && values.position?.trim()) {
        payload.position = values.position.trim();
      }

      if (Object.keys(payload).length === 0) {
        setShowConfirmation(false);
        onSubmit(values);
        return;
      }

      await updateUserProfile(payload);

      setShowConfirmation(false);
      onSubmit(values);
      await getUser();
    } catch (error) {
      console.error("Failed to update profile:", error);
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleCancelUpdate = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Modal
        title="Pledge"
        open={open}
        onClose={onClose}
        showClose={true}
        loading={loading}
      >
        <Box
          sx={{
            overflowY: "auto",
            paddingRight: "8px",
          }}
        >
          <Box className={styles.form}>
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                Select
              </Typography>
              <Controller
                name={UserInputs.USER_TYPE}
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        value="Individual"
                        checked={field.value === "Individual"}
                        onChange={() => field.onChange("Individual")}
                      />
                      <Typography fontSize={14}>Individual</Typography>
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        value="Corporate"
                        checked={field.value === "Corporate"}
                        onChange={() => field.onChange("Corporate")}
                      />
                      <Typography fontSize={14}>Corporate</Typography>
                    </label>
                  </Box>
                )}
              />
              {errors[UserInputs.USER_TYPE] && (
                <Typography color="error" fontSize={12} mt={0.5}>
                  {errors[UserInputs.USER_TYPE]?.message}
                </Typography>
              )}
            </Box>

            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Box className={styles.form}>
                <Input
                  fullWidth
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email id"
                  {...register(UserInputs.EMAIL_ADDRESS)}
                  error={!!errors[UserInputs.EMAIL_ADDRESS]}
                  helperText={errors[UserInputs.EMAIL_ADDRESS]?.message}
                />
                <Input
                  fullWidth
                  label="Name"
                  placeholder="Enter your name"
                  {...register(UserInputs.NAME)}
                  error={!!errors[UserInputs.NAME]}
                  helperText={errors[UserInputs.NAME]?.message}
                />

                {userType === "Corporate" && (
                  <Input
                    fullWidth
                    label="Position"
                    placeholder="Enter position"
                    {...register(UserInputs.POSITION)}
                    error={!!errors[UserInputs.POSITION]}
                    helperText={errors[UserInputs.POSITION]?.message}
                  />
                )}

                <Input
                  fullWidth
                  label="Contact Number"
                  type="tel"
                  placeholder="Enter your contact number"
                  {...register(UserInputs.CONTACT_NUMBER)}
                  error={!!errors[UserInputs.CONTACT_NUMBER]}
                  helperText={errors[UserInputs.CONTACT_NUMBER]?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Typography fontSize={14}>+91</Typography>
                      ),
                    },
                  }}
                />

                {isPanRequired && (
                  <Input
                    fullWidth
                    label="PAN Number"
                    placeholder="Enter your PAN"
                    {...register(UserInputs.PAN)}
                    error={!!errors[UserInputs.PAN]}
                    helperText={errors[UserInputs.PAN]?.message}
                  />
                )}
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                loading={isSubmitting || loading || updatingProfile}
                disabled={isSubmitting || loading || updatingProfile}
              >
                {isSubmitting || loading ? "Processing..." : "Submit"}
              </Button>
            </form>
          </Box>
        </Box>
      </Modal>

      <DefaultDetailsConfirmationModal
        open={showConfirmation}
        onConfirm={handleConfirmUpdate}
        onCancel={handleCancelUpdate}
        loading={updatingProfile}
      />
    </>
  );
};

export default DetailsModal;
